import { FLOWERS } from '../../game/flowers';
import { canFulfill } from '../../game/orders';
import { levelProgress } from '../../game/progression';
import { formatClock, isNight } from '../../game/simulation';
import { openPanel, togglePanel, ui, type PanelId } from '../store';
import { useGameState, useServices } from '../context';
import { Hotbar } from './Hotbar';
import { Chat } from './Chat';

function weatherIcon(raining: boolean, rainbow: boolean, night: boolean): string {
  if (raining) return '🌧️';
  if (rainbow) return '🌈';
  return night ? '🌙' : '☀️';
}

function TopBar() {
  const state = useGameState();
  const progress = levelProgress(state.xp);
  const night = isNight(state.dayTime);
  const featured = FLOWERS[state.market.featured];
  return (
    <div class="topbar">
      <div class="chip farm-chip" title={state.farmName}>
        <span class="farm-name">{state.farmName}</span>
        <span class="clock">
          {weatherIcon(state.weather.raining, state.weather.rainbowLeft > 0, night)} Day {state.day}{' '}
          · {formatClock(state.dayTime)}
        </span>
      </div>
      <div class="chip coins-chip" aria-label={`${state.coins} coins`}>
        <span class="coin" aria-hidden="true">
          🪙
        </span>
        <span class="coins-value">{state.coins.toLocaleString()}</span>
      </div>
      <div class="chip level-chip" aria-label={`Level ${progress.level}`}>
        <span class="level-badge">Lv {progress.level}</span>
        <div
          class="xp-bar"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={progress.needed}
          aria-valuenow={progress.current}
        >
          <div class="xp-fill" style={{ width: `${Math.round(progress.fraction * 100)}%` }} />
        </div>
      </div>
      <div class="chip featured-chip" title="Today's featured flower sells for extra">
        ⭐ {featured.emoji} <span class="hide-small">{featured.name}</span>
      </div>
    </div>
  );
}

interface MenuButton {
  id: PanelId;
  icon: string;
  label: string;
}

const MENU: readonly MenuButton[] = [
  { id: 'market', icon: '🏪', label: 'Market' },
  { id: 'orders', icon: '📜', label: 'Orders' },
  { id: 'almanac', icon: '📖', label: 'Almanac' },
  { id: 'friends', icon: '👥', label: 'Friends' },
  { id: 'help', icon: '❓', label: 'Help' },
  { id: 'settings', icon: '⚙️', label: 'Settings' },
];

function SideMenu() {
  const { play, settings, updateSettings, audio } = useServices();
  const state = useGameState();
  const ready = state.orders.filter((o) => canFulfill(state, o)).length;
  const visitors = ui.players.value.length;

  const open = (id: PanelId) => {
    audio.play('click');
    // The market is a place: walk there. Everything else opens right away.
    if (id === 'market') {
      openPanel(null);
      play.useProp('market');
    } else togglePanel(id);
  };

  const muted = settings.value.muted;
  return (
    <nav class="side-menu" aria-label="Game menu">
      {MENU.map((item) => (
        <button
          key={item.id}
          class="menu-button"
          onClick={() => open(item.id)}
          aria-label={item.label}
          title={item.label}
        >
          <span aria-hidden="true">{item.icon}</span>
          {item.id === 'orders' && ready > 0 && <span class="badge">{ready}</span>}
          {item.id === 'friends' && visitors > 1 && (
            <span class="badge badge-blue">{visitors}</span>
          )}
          <span class="menu-label">{item.label}</span>
        </button>
      ))}
      <button
        class="menu-button"
        onClick={() => updateSettings({ muted: !muted })}
        aria-label={muted ? 'Unmute' : 'Mute'}
        aria-pressed={muted}
        title={muted ? 'Unmute' : 'Mute'}
      >
        <span aria-hidden="true">{muted ? '🔇' : '🔊'}</span>
        <span class="menu-label">{muted ? 'Muted' : 'Sound'}</span>
      </button>
    </nav>
  );
}

function VisitBanner() {
  const { net } = useServices();
  const status = ui.net.value;
  if (status.mode === 'client') {
    return (
      <div class="visit-banner">
        <span>
          🧺 Visiting <b>{status.hostName}</b>'s farm
        </span>
        <button class="pill-button" onClick={() => net.leave()}>
          Go home
        </button>
      </div>
    );
  }
  if (status.mode === 'host') {
    return (
      <div class="visit-banner host">
        <span>
          🌼 Farm open · code <b class="code">{status.code}</b>
        </span>
        <button class="pill-button" onClick={() => openPanel('friends')}>
          Invite
        </button>
      </div>
    );
  }
  return null;
}

export function Hud() {
  const multiplayer = ui.net.value.mode === 'host' || ui.net.value.mode === 'client';
  return (
    <div class="hud">
      <TopBar />
      <VisitBanner />
      <SideMenu />
      <Hotbar />
      {multiplayer && <Chat />}
    </div>
  );
}
